















import Vue from 'vue';
import { mapGetters } from 'vuex';
import { merge, cloneDeep, debounce, truncate, filter, reverse } from 'lodash';

import Chart from 'vue-echarts';
import 'zrender/lib/svg/svg';
import 'echarts/lib/chart/heatmap';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/visualMap';
import 'echarts/lib/component/grid';
import LoadingSpinner from '@reciprocity/loading-spinner';

import { HeatmapLabels, Threshold, HeatmapCell } from './heatmap_types';
import chartConfig from './heatmap_config';
import { keysToCamel, keysToSnake } from '@/utils';
import { getRequest } from '../../../utils/client/client';

export default Vue.extend({
  components: {
    Chart,
    LoadingSpinner,
  },
  props: {
    thresholds: {
      type: Array as () => Threshold[],
      required: true,
    },
    canLoadData: {
      type: Boolean,
      default: false,
    },
    setSelected: {
      type: Array,
      default: () => [],
    },
    flip: {
      type: Boolean,
      default: false,
    },
  },
  data: function() {
    return {
      labels: <HeatmapLabels>{},
      values: <HeatmapCell>[],
      emptyValues: <HeatmapCell>[],
      isLoading: true,
      selected: <HeatmapCell>[],
    };
  },
  computed: {
    ...mapGetters(['routes']),
    ...mapGetters('heatmap', ['selectedFilters', 'selectedScore', 'selectedVectorX', 'selectedVectorY']),
    chartConfig() {
      return {
        xAxis: {
          name: truncate(this.selectedVectorX.title, { length: 30, omission: '...' }).toUpperCase(),
        },
        yAxis: {
          name: truncate(this.selectedVectorY.title, { length: 30, omission: '...' }).toUpperCase(),
        },
      };
    },
    chartOptions(): any {
      const values = cloneDeep(this.values);
      const emptyValues = cloneDeep(this.emptyValues);
      const xAxisCategories = cloneDeep(this.labels.xAxisCategories);
      const yAxisCategories = cloneDeep(this.labels.yAxisCategories);

      if (this.flip) {
        this.flipCells(values);
        this.flipCells(emptyValues);
        reverse(xAxisCategories);
        reverse(yAxisCategories);
      }

      return merge(
        {},
        chartConfig,
        {
          visualMap: {
            pieces: this.handleThresholds,
          },
          series: [
            {
              data: values,
            },
            {
              data: emptyValues,
            },
          ],
          xAxis: {
            data: xAxisCategories,
          },
          yAxis: {
            data: yAxisCategories,
          },
        },
        this.chartConfig
      );
    },
    handleThresholds(): Array<Threshold> {
      let thresholds: Array<Threshold> = [];
      if (this.thresholds.length) {
        thresholds = cloneDeep(this.thresholds);
        const lastThreshold = this.thresholds[this.thresholds.length - 1];
        thresholds.push({
          min: lastThreshold.max,
          color: lastThreshold.color,
        });
      }
      return thresholds;
    },
  },
  watch: {
    canLoadData(value, old) {
      // Load of options should be postponed until parent is ready:
      if (old || !value) return;
      this.debounceFetchHeatmapData();
    },
    isLoading: {
      handler(val) {
        // Notify parent:
        this.$emit('stateChanged', val);
      },
      immediate: true,
    },
    selectedScore: {
      handler() {
        this.debounceFetchHeatmapData();
      },
      deep: true,
      immediate: true,
    },
    selectedVectorX: {
      handler() {
        this.debounceFetchHeatmapData();
      },
      deep: true,
      immediate: true,
    },
    selectedVectorY: {
      handler() {
        this.debounceFetchHeatmapData();
      },
      deep: true,
      immediate: true,
    },
    selectedFilters: {
      handler() {
        this.debounceFetchHeatmapData();
      },
      deep: true,
      immediate: true,
    },
    setSelected: {
      handler(indexes) {
        this.selected = [];
        for (let index of indexes) {
          this.selectItem(index, false);
        }
      },
      deep: true,
    },
    async flip() {
      await this.$nextTick();
      for (let cell of this.selected) {
        this.highlightCell(cell.cellIndex);
      }
    },
  },
  created() {
    this.$parent.$on('selectionToggle', this.selectionToggle);
  },
  methods: {
    onCellClick(item) {
      this.selectItem(item.dataIndex);
    },
    isSelected(cellIndex) {
      return this.selected.findIndex(c => c.cellIndex === cellIndex) !== -1;
    },
    flipCells(cells) {
      const maxX = this.labels.xAxisCategories.length - 1;
      const maxY = this.labels.yAxisCategories.length - 1;
      for (let i = 0; i < cells.length; i++) {
        cells[i].value[0] = maxX - cells[i].value[0];
        cells[i].value[1] = maxY - cells[i].value[1];
      }
    },
    selectItem(cellIndex: number) {
      let item = this.values[cellIndex];
      item = cloneDeep(item);
      item.cellIndex = cellIndex;

      if (this.isSelected(item.cellIndex)) {
        this.toggleCell(item, false, null);
      } else {
        this.toggleCell(item, true, cellIndex);
      }
    },
    highlightCell(cellIndex) {
      this.$refs.chart.dispatchAction({
        type: 'highlight',
        seriesIndex: 0,
        dataIndex: cellIndex,
      });
    },
    parseMultipleOptions(options): Function {
      return (options || []).length ? options.join(',') : null;
    },
    async fetchHeatmapData() {
      if ((this.selectedScore && !this.selectedScore.id) || !this.selectedVectorX.id || !this.selectedVectorY.id) {
        return;
      }

      this.isLoading = true;

      try {
        let dataResp = await getRequest({
          url: this.routes.riskHeatmapData,
          params: keysToSnake({
            scoreId: this.selectedScore.id,
            xVectorId: this.selectedVectorX.id,
            yVectorId: this.selectedVectorY.id,
            objectType: this.selectedFilters.objectType,
            objectId: this.selectedFilters.objectId,
            statuses: this.parseMultipleOptions(this.selectedFilters.statuses),
            owners: this.parseMultipleOptions(this.selectedFilters.owners),
            tags: this.parseMultipleOptions(this.selectedFilters.tags),
          }),
        });
        dataResp = keysToCamel(dataResp);
        this.values = dataResp.values;
        this.emptyValues = dataResp.emptyValues;
      } catch (error) {
        throw error;
      }
    },
    async fetchHeatmapLabels() {
      if (!this.selectedVectorX.id || !this.selectedVectorY.id) return;
      try {
        const labelsData = await getRequest({
          url: this.routes.riskHeatmapLabels,
          params: keysToSnake({
            xVectorId: this.selectedVectorX.id,
            yVectorId: this.selectedVectorY.id,
          }),
        });
        this.labels = keysToCamel(labelsData);
      } catch (error) {
        throw error;
      }
    },
    debounceFetchHeatmapData: debounce(async function() {
      if (!this.canLoadData) return;

      await this.fetchHeatmapData();
      await this.fetchHeatmapLabels();
      this.isLoading = false;
      this.$nextTick(() => {
        this.$emit('loaded', 'heatmap');
      });
    }, 10),

    selectionToggle() {
      if (this.values.length === this.selected.length) {
        this.deselectAllCells();
      } else {
        this.selectAllCells();
      }
    },

    selectAllCells() {
      this.values.forEach((cell, cellIndex) => {
        this.toggleCell(cell, true, cellIndex);
      });
    },

    deselectAllCells() {
      const selectedCells = cloneDeep(this.selected);
      selectedCells.forEach(cell => {
        this.toggleCell(cell, false, null);
      });
    },

    toggleCell(cell, select, cellIndex) {
      const action = select ? 'highlight' : 'downplay';
      const index = cellIndex !== null ? cellIndex : cell.cellIndex;
      if (this.$refs.chart) {
        this.$refs.chart.dispatchAction({
          type: action,
          seriesIndex: 0,
          dataIndex: index,
        });
      }
      if (select) {
        if (!this.isSelected(index)) {
          cell.cellIndex = index;
          this.selected.push(cell);
        }
      } else {
        this.selected = filter(this.selected, ({ cellIndex }) => cellIndex !== index);
      }
      this.$emit('itemSelected', this.selected);
    },
  },
});
